import { useState, type JSX, type ReactNode } from 'react';
import { useStore } from '@/state/store';
import { EXAMPLES } from '@/examples/library';

export function Dialog({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}): JSX.Element {
  return (
    <div className="overlay" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <h2>{title}</h2>
        <div className="content">{children}</div>
        <div className="foot">
          {footer}
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function ExamplesDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const loadExample = useStore((s) => s.loadExample);
  return (
    <Dialog title="Example crease patterns" onClose={onClose}>
      <div className="example-grid">
        {EXAMPLES.map((e) => (
          <button
            key={e.id}
            className="example-card"
            data-testid={`example-${e.id}`}
            onClick={() => {
              loadExample(e.id);
              onClose();
            }}
          >
            <div className="n">{e.name}</div>
            <div className="d">{e.description}</div>
            <div className="m">{e.mathematics}</div>
            <div className="m">Recommended solver: {e.recommendedSolver}</div>
          </button>
        ))}
      </div>
    </Dialog>
  );
}

export function NewSheetDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const newSheet = useStore((s) => s.newSheet);
  const [width, setWidth] = useState(1);
  const [height, setHeight] = useState(1);
  return (
    <Dialog
      title="New sheet"
      onClose={onClose}
      footer={
        <button
          className="btn primary"
          onClick={() => {
            newSheet(Math.max(0.01, width), Math.max(0.01, height));
            onClose();
          }}
        >
          Create
        </button>
      }
    >
      <p>
        Paper is measured in abstract units; the solver's tolerances are scaled to the sheet, so the
        numbers only matter relative to each other.
      </p>
      <div className="row">
        <span className="k">Width</span>
        <input
          type="number"
          step="0.1"
          min="0.01"
          value={width}
          onChange={(e) => setWidth(Number(e.target.value))}
        />
      </div>
      <div className="row">
        <span className="k">Height</span>
        <input
          type="number"
          step="0.1"
          min="0.01"
          value={height}
          onChange={(e) => setHeight(Number(e.target.value))}
        />
      </div>
      <div className="btn-row">
        {[
          ['Square', 1, 1],
          ['A-series (√2)', 1, Math.SQRT2],
          ['2 : 1', 2, 1],
          ['3 : 4', 3, 4],
        ].map(([label, w, h]) => (
          <button
            key={String(label)}
            className="btn tiny"
            onClick={() => {
              setWidth(Number(w));
              setHeight(Number(h));
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </Dialog>
  );
}

export function AboutDialog({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <Dialog title="About fold.app" onClose={onClose}>
      <p>
        fold.app is a browser-based computational-origami workbench: a crease-pattern editor, a
        foldability analyser and two folding solvers, with no backend.
      </p>

      <h3>What the analysis does and does not claim</h3>
      <p>
        Kawasaki's and Maekawa's theorems are <b>local necessary</b> conditions for flat folding.
        Passing them at every interior vertex does not prove that a whole crease pattern folds flat —
        that decision problem is NP-hard. fold.app reports what it verifies and nothing more.
      </p>

      <h3>References</h3>
      <p>
        E. D. Demaine and J. O'Rourke, <i>Geometric Folding Algorithms: Linkages, Origami,
        Polyhedra</i>, Cambridge University Press, 2007 — and MIT 6.849 / 6.5310, whose lectures are
        the background for the whole analysis layer.
      </p>
      <p>
        E. D. Demaine, J. Ku and R. J. Lang, <i>The FOLD file format</i>. fold.app reads and
        writes FOLD 1.2.
      </p>
      <p>
        A. Ghassaei, E. D. Demaine and N. Gershenfeld, <i>Fast, Interactive Origami Simulation using
        GPU Computation</i>, 7OSME, 2018 — the source of the bar-and-hinge formulation this app's
        compliant solver follows.
      </p>
      <p>
        T. Tachi, <i>Simulation of Rigid Origami</i>, Origami<sup>4</sup>, 2009 — the projection
        method used by the rigid solver, and the per-vertex loop-closure constraint.
      </p>
      <p>
        T. Kawasaki (1989) and J. Justin (1986) on the alternating sector-angle condition; J. Maekawa,
        published by K. Husimi (1989), on |M − V| = 2.
      </p>
      <p>
        M. Bern and B. Hayes, <i>The complexity of flat origami</i>, SODA 1996 — why global flat
        foldability is NP-hard.
      </p>
      <p>
        M. Macklin, M. Müller and N. Chentanez, <i>XPBD: Position-Based Simulation of Compliant
        Constrained Dynamics</i>, MIG 2016 — the integrator.
      </p>
      <p>
        J. R. Shewchuk, <i>Adaptive Precision Floating-Point Arithmetic and Fast Robust Geometric
        Predicates</i>, 1997 — the orientation predicate.
      </p>
      <p>
        R. Tamstorf and E. Grinspun, <i>Discrete bending forces and their gradients</i>, 2013, and
        R. Bridson et al. (2003) — the dihedral-angle gradient.
      </p>
      <p>
        T. Möller, <i>A fast triangle-triangle intersection test</i>, 1997 — narrow-phase collision.
      </p>
      <p>
        Ear clipping uses <b>earcut</b> by Mapbox (ISC licence). Rendering uses <b>three.js</b> (MIT).
      </p>

      <h3>Licence</h3>
      <p>
        fold.app is MIT-licensed. No source code was copied from the projects above; they were
        used as the mathematical and architectural reference.
      </p>
    </Dialog>
  );
}
